import { useState } from 'react';
import { useApi } from '../../engine/api/context';
import { useInvalidateResource } from '../../engine/api/hooks';
import { ApiError } from '../../engine/api/client';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, RadioGroup, Switch } from '../../engine/design/primitives/Input';
import { Badge, Banner } from '../../engine/design/primitives/Misc';
import { Icon } from '../../engine/design/icons';
import { useToast } from '../../engine/design/primitives/Toast';

// The mailbox setup wizard — the create-drawer body for /email/mailboxes (ScreenDef.form.component).
// kind → credentials / OAuth app → create + test → queue mapping. Secrets are sent once, as
// write-only fields the API encrypts; the created row never carries them back.

type Kind = 'imap' | 'smtp' | 'graph' | 'gmail';
const KINDS: { value: Kind; label: string; hint: string }[] = [
  { value: 'imap', label: 'IMAP + SMTP', hint: 'Any mail server: poll with IMAP, send with SMTP.' },
  { value: 'graph', label: 'Microsoft 365', hint: 'Connect a Microsoft mailbox through an Entra app (OAuth).' },
  { value: 'gmail', label: 'Gmail', hint: 'Connect a Google Workspace mailbox through a Google Cloud app (OAuth).' },
  { value: 'smtp', label: 'SMTP only', hint: 'Outbound only — no inbound feed.' },
];

interface Check {
  name: string;
  ok: boolean;
  detail: string;
}
type Row = Record<string, unknown> & { id: string };

const STEPS = ['Kind', 'Connection', 'Test', 'Routing'];

export function MailboxWizard({ onDone, onCancel }: { onDone: (row?: Record<string, unknown>) => void; onCancel: () => void; id?: string }) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateResource();
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<Kind>('imap');
  const [f, setF] = useState({
    name: '',
    address: '',
    username: '',
    password: '',
    imap_host: '',
    imap_port: '993',
    imap_tls: true,
    smtp_host: '',
    smtp_port: '465',
    smtp_tls: true,
    oauth_tenant: '',
    oauth_client_id: '',
    client_secret: '',
    queue_id: '',
    inbound_enabled: true,
    outbound_enabled: true,
    delete_after_import: false,
    default_from_name: '',
  });
  const [row, setRow] = useState<Row | null>(null);
  const [checks, setChecks] = useState<Check[] | null>(null);
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((s) => ({ ...s, [k]: v }));
  const oauth = kind === 'graph' || kind === 'gmail';

  const body = () => {
    const b: Record<string, unknown> = { name: f.name, address: f.address, kind, inbound_enabled: kind !== 'smtp' && f.inbound_enabled, outbound_enabled: f.outbound_enabled, delete_after_import: f.delete_after_import };
    if (f.default_from_name) b['default_from_name'] = f.default_from_name;
    if (kind === 'imap' || kind === 'smtp') {
      if (f.username) b['username'] = f.username;
      if (f.password) b['password'] = f.password;
      if (kind === 'imap') Object.assign(b, { imap_host: f.imap_host || null, imap_port: f.imap_port ? Number(f.imap_port) : null, imap_tls: f.imap_tls });
      Object.assign(b, { smtp_host: f.smtp_host || null, smtp_port: f.smtp_port ? Number(f.smtp_port) : null, smtp_tls: f.smtp_tls });
    } else {
      Object.assign(b, { oauth_tenant: f.oauth_tenant || null, oauth_client_id: f.oauth_client_id || null });
      if (f.client_secret) b['client_secret'] = f.client_secret;
    }
    return b;
  };

  const createAndTest = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = row ?? ((await api.resource<Row>('mailboxes').create(body())) as Row);
      setRow(created);
      invalidate('mailboxes');
      if (oauth) {
        const r = await api.resource('mailboxes').action<{ url: string }>('oauth-start', { id: created.id });
        setOauthUrl(r.url);
      } else {
        const r = await api.resource('mailboxes').action<{ ok: boolean; checks: Check[] }>('test', { id: created.id });
        setChecks(r.checks);
      }
      setStep(2);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.resource<Row>('mailboxes').update(row.id, { queue_id: f.queue_id || null, inbound_enabled: kind !== 'smtp' && f.inbound_enabled, outbound_enabled: f.outbound_enabled, delete_after_import: f.delete_after_import });
      invalidate('mailboxes');
      toast({ title: `Mailbox ${f.name} ready`, detail: oauth && !oauthUrl ? undefined : oauth ? 'Finish the account connection in the provider window.' : undefined });
      onDone(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  };

  const canContinue = step === 0 ? true : step === 1 ? Boolean(f.name && f.address && (oauth ? f.oauth_client_id : true)) : true;

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-5)' }} aria-label="Mailbox setup">
      <ol className="hf-row" style={{ gap: 'var(--s-3)', listStyle: 'none', padding: 0, margin: 0 }} aria-label="Steps">
        {STEPS.map((s, i) => (
          <li key={s} aria-current={i === step ? 'step' : undefined} className="hf-row" style={{ gap: 6, color: i === step ? 'var(--ink)' : 'var(--ink-3)', fontSize: 'var(--t-12)' }}>
            <Badge tone={i < step ? 'pos' : i === step ? 'accent' : 'muted'} dot={false}>
              {i + 1}
            </Badge>
            {s}
          </li>
        ))}
      </ol>

      {error && <Banner tone="error">{error}</Banner>}

      {step === 0 && (
        <div className="hf-stack">
          <RadioGroup name="kind" value={kind} onChange={(v) => setKind(v as Kind)} options={KINDS.map((k) => ({ value: k.value, label: k.label }))} aria-label="Mailbox kind" />
          <p className="hf-muted" style={{ fontSize: 'var(--t-13)' }}>{KINDS.find((k) => k.value === kind)?.hint}</p>
        </div>
      )}

      {step === 1 && (
        <div className="hf-form-grid">
          <Field label="Name" required>
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Support" />
          </Field>
          <Field label="Address" required>
            <Input type="email" value={f.address} onChange={(e) => set('address', e.target.value)} placeholder="support@example.com" />
          </Field>
          <Field label="From name" help="Shown as the sender name on outbound mail.">
            <Input value={f.default_from_name} onChange={(e) => set('default_from_name', e.target.value)} placeholder="Acme Support" />
          </Field>
          {!oauth && (
            <>
              <Field label="Username" help="Defaults to the address.">
                <Input value={f.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" />
              </Field>
              <Field label="Password" help="Stored encrypted; never shown again.">
                <Input type="password" value={f.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" />
              </Field>
              {kind === 'imap' && (
                <>
                  <Field label="IMAP host" required>
                    <Input value={f.imap_host} onChange={(e) => set('imap_host', e.target.value)} placeholder="imap.example.com" />
                  </Field>
                  <Field label="IMAP port">
                    <Input inputMode="numeric" value={f.imap_port} onChange={(e) => set('imap_port', e.target.value)} />
                  </Field>
                  <Field label="IMAP TLS" inline>
                    <Switch checked={f.imap_tls} onChange={(e) => set('imap_tls', e.target.checked)} />
                  </Field>
                </>
              )}
              <Field label="SMTP host">
                <Input value={f.smtp_host} onChange={(e) => set('smtp_host', e.target.value)} placeholder="smtp.example.com" />
              </Field>
              <Field label="SMTP port">
                <Input inputMode="numeric" value={f.smtp_port} onChange={(e) => set('smtp_port', e.target.value)} />
              </Field>
              <Field label="SMTP TLS" inline>
                <Switch checked={f.smtp_tls} onChange={(e) => set('smtp_tls', e.target.checked)} />
              </Field>
            </>
          )}
          {oauth && (
            <>
              {kind === 'graph' && (
                <Field label="Directory (tenant) id" help="Your Entra tenant id, or 'common'.">
                  <Input value={f.oauth_tenant} onChange={(e) => set('oauth_tenant', e.target.value)} placeholder="contoso.onmicrosoft.com" />
                </Field>
              )}
              <Field label="Client id" required>
                <Input value={f.oauth_client_id} onChange={(e) => set('oauth_client_id', e.target.value)} autoComplete="off" />
              </Field>
              <Field label="Client secret" help="Stored encrypted; never shown again.">
                <Input type="password" value={f.client_secret} onChange={(e) => set('client_secret', e.target.value)} autoComplete="new-password" />
              </Field>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="hf-stack">
          {oauthUrl ? (
            <Banner tone="info" icon="plug-zap" action={<Button size="sm" variant="primary" icon="external-link" onClick={() => window.open(oauthUrl, '_blank', 'noopener')}>Connect account</Button>}>
              The mailbox is saved. Connect the {kind === 'graph' ? 'Microsoft' : 'Google'} account to grant read/send access; you will be brought back here.
            </Banner>
          ) : (
            <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Connection checks">
              {(checks ?? []).map((c) => (
                <li key={c.name} className="hf-row" style={{ alignItems: 'flex-start' }}>
                  <Icon name={c.ok ? 'check-circle' : 'x-circle'} size={16} style={{ color: c.ok ? 'var(--pos)' : 'var(--neg)', marginTop: 2 }} label={c.ok ? 'ok' : 'failed'} />
                  <div>
                    <strong style={{ textTransform: 'uppercase', fontSize: 'var(--t-12)' }}>{c.name}</strong>
                    <div className="hf-muted" style={{ fontSize: 'var(--t-13)' }}>{c.detail}</div>
                  </div>
                </li>
              ))}
              {checks && checks.length === 0 && <li className="hf-muted">Nothing to test for this configuration.</li>}
            </ul>
          )}
          {checks && checks.some((c) => !c.ok) && <Banner tone="warn">Some checks failed. You can still continue and fix the settings on the mailbox page.</Banner>}
        </div>
      )}

      {step === 3 && (
        <div className="hf-form-grid">
          <Field label="Queue id" help="Inbound mail from this mailbox lands in this service-desk queue (optional)." span={2}>
            <Input value={f.queue_id} onChange={(e) => set('queue_id', e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </Field>
          {kind !== 'smtp' && (
            <Field label="Poll for inbound mail" inline>
              <Switch checked={f.inbound_enabled} onChange={(e) => set('inbound_enabled', e.target.checked)} />
            </Field>
          )}
          <Field label="Send outbound mail" inline>
            <Switch checked={f.outbound_enabled} onChange={(e) => set('outbound_enabled', e.target.checked)} />
          </Field>
          {kind !== 'smtp' && (
            <Field label="Delete after import" help="Off by default: imported mail is marked read, never removed." inline>
              <Switch checked={f.delete_after_import} onChange={(e) => set('delete_after_import', e.target.checked)} />
            </Field>
          )}
        </div>
      )}

      <div className="hf-form-actions">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {step > 0 && step < 2 && (
          <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy}>
            Back
          </Button>
        )}
        {step < 1 && (
          <Button variant="primary" onClick={() => setStep(1)}>
            Continue
          </Button>
        )}
        {step === 1 && (
          <Button variant="primary" busy={busy} disabled={!canContinue} onClick={createAndTest}>
            {oauth ? 'Save and connect' : 'Save and test'}
          </Button>
        )}
        {step === 2 && (
          <Button variant="primary" onClick={() => setStep(3)} disabled={busy}>
            Continue
          </Button>
        )}
        {step === 3 && (
          <Button variant="primary" busy={busy} onClick={finish}>
            Finish
          </Button>
        )}
      </div>
    </div>
  );
}
